import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X, Sparkles } from "lucide-react";
import { API_BASE } from "../lib/api";

const STORAGE_SID = "js_chatbot_session_id";
const STORAGE_MSGS = "js_chatbot_messages";

const WELCOME = {
  role: "assistant",
  text: "नमो नमः! I'm your Sanskrit learning assistant. Ask me to translate a phrase, explain a word, or suggest how to start speaking Sanskrit. 🙏",
};

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try { const s = localStorage.getItem(STORAGE_MSGS); if (s) return JSON.parse(s); } catch {}
    return [WELCOME];
  });
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(() => { try { return localStorage.getItem(STORAGE_SID) || null; } catch { return null; } });
  const [streaming, setStreaming] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_MSGS, JSON.stringify(messages.slice(-50))); } catch {}
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => { if (sessionId) try { localStorage.setItem(STORAGE_SID, sessionId); } catch {} }, [sessionId]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages(m => [...m, { role: "user", text }, { role: "assistant", text: "", pending: true }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API_BASE}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: "Request failed" }));
        setMessages(m => {
          const copy = [...m]; copy[copy.length - 1] = { role: "assistant", text: err.detail || "Something went wrong. Please try again." };
          return copy;
        });
        setStreaming(false); return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const data = JSON.parse(payload);
            if (data.session_id) setSessionId(data.session_id);
            if (data.remaining != null) setRemaining(data.remaining);
            if (data.error) {
              setMessages(m => {
                const copy = [...m]; copy[copy.length - 1] = { role: "assistant", text: "Sorry, I couldn't respond. Please try again." };
                return copy;
              });
            }
            if (data.t) {
              setMessages(m => {
                const copy = [...m];
                const last = { ...copy[copy.length - 1] };
                last.text = (last.text || "") + data.t;
                last.pending = false;
                copy[copy.length - 1] = last;
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch (err) {
      setMessages(m => {
        const copy = [...m]; copy[copy.length - 1] = { role: "assistant", text: "Could not reach the server. Please check your connection." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const resetChat = () => {
    setMessages([WELCOME]);
    setSessionId(null);
    try { localStorage.removeItem(STORAGE_SID); localStorage.removeItem(STORAGE_MSGS); } catch {}
  };

  return (
    <>
      <button onClick={() => setOpen(o => !o)} data-testid="chatbot-toggle"
        aria-label={open ? "Close chat" : "Open Sanskrit chatbot"}
        className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full shadow-xl flex items-center justify-center text-white transition-transform hover:scale-105"
        style={{ background: "var(--js-primary)" }}>
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {open && (
        <div data-testid="chatbot-panel"
          className="fixed bottom-24 right-3 sm:right-5 z-40 w-[calc(100vw-24px)] sm:w-96 max-w-md h-[75vh] sm:h-[560px] bg-white border border-[var(--js-border)] rounded-xl shadow-2xl flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--js-border)] flex items-center gap-2" style={{ background: "#F1EADD" }}>
            <Sparkles size={16} className="text-[var(--js-primary)]" />
            <div className="flex-1">
              <div className="font-heading text-base text-[var(--js-primary)]">Sanskrit Assistant</div>
              <div className="text-[11px] text-[var(--js-muted)]">Ask about Sanskrit, courses or programmes</div>
            </div>
            <button onClick={resetChat} data-testid="chatbot-reset" className="text-xs text-[var(--js-muted)] hover:text-[var(--js-primary)]" title="New chat">Reset</button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-[var(--js-primary)] text-white rounded-br-md" : "bg-[var(--js-bg)] text-[var(--js-text)] rounded-bl-md border border-[var(--js-border)]"}`}
                     data-testid={`chat-msg-${m.role}`}>
                  {m.pending ? <span className="opacity-60">Thinking…</span> : m.text}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={send} className="border-t border-[var(--js-border)] p-3 flex items-center gap-2">
            <input value={input} onChange={e => setInput(e.target.value)} disabled={streaming}
              placeholder="Ask in English, Hindi, Odia or Sanskrit…"
              data-testid="chatbot-input"
              className="flex-1 px-3 py-2 rounded-full border border-[var(--js-border)] text-sm focus:outline-none focus:border-[var(--js-primary)] disabled:opacity-60" />
            <button type="submit" disabled={streaming || !input.trim()} data-testid="chatbot-send"
              className="w-10 h-10 rounded-full flex items-center justify-center text-white disabled:opacity-50"
              style={{ background: "var(--js-primary)" }}>
              <Send size={16} />
            </button>
          </form>
          {remaining != null && remaining <= 5 && (
            <div className="px-3 pb-2 text-[11px] text-[var(--js-muted)] text-center">{remaining} free messages left today</div>
          )}
        </div>
      )}
    </>
  );
}
